import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  selectAuth,
  selectTokenVersion,
  refreshAccessToken,
  verifyToken,
} from '../store/authSlice';
import { scheduleTokenRefresh } from '../utils/scheduleTokenRefresh';
import { decodeJwt } from '../utils/jwt';

const TOKEN_EXPIRY_BUFFER_MS = 90_000;

/**
 * Фоновый жизненный цикл accessToken:
 * - плановый refresh до exp;
 * - refresh при возврате на вкладку, если токен скоро истечёт;
 * - не блокирует UI (silent refresh).
 */
export function useTokenLifecycle() {
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector(selectAuth);
  const tokenVersion = useAppSelector(selectTokenVersion);
  const scheduledRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      scheduledRef.current = false;
      return;
    }
    if (scheduledRef.current) return;
    scheduledRef.current = true;
    dispatch(scheduleTokenRefresh());
  }, [isAuthenticated, dispatch, tokenVersion]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      const payload = decodeJwt(token);
      if (!payload?.exp) {
        void dispatch(verifyToken());
        return;
      }

      const expMs = payload.exp * 1000;
      if (expMs - Date.now() > TOKEN_EXPIRY_BUFFER_MS) {
        return;
      }

      void dispatch(refreshAccessToken('visibility'));
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isAuthenticated, dispatch]);
}
